import React from "react";
import { Pressable, Text, View } from "react-native";
import {
  Edit2,
  Lock,
  Profile,
  ReceiptText,
  Subtitle,
} from "iconsax-react-nativejs";
import { useTranslation } from "react-i18next";
import { useRouter } from "expo-router";
import { formatUserName } from "@/src/utils/string.utils";
import { useTheme } from "@/src/styles/theme-provider";
import { GroupDetailData } from "@/src/models/group.model";
import { useBlocksUserByIdQuery } from "@/src/services/users.blocks.api";

type GroupInfoCardProps = Readonly<{
  group: GroupDetailData;
  isAdmin?: boolean;
}>;

type InfoRowProps = Readonly<{
  icon: React.ReactNode;
  label: string;
  value: string;
}>;

const InfoRow = ({ icon, label, value }: InfoRowProps) => (
  <View className="flex-row items-start gap-3 py-2">
    <View className="mt-0.5">{icon}</View>
    <View className="flex-1">
      <Text className="text-xs text-slate mb-0.5">{label}</Text>
      <Text className="text-sm text-onyx" numberOfLines={4}>
        {value}
      </Text>
    </View>
  </View>
);

const GroupInfoCard = ({ group, isAdmin }: GroupInfoCardProps) => {
  const { t } = useTranslation();
  const { themeVars } = useTheme();
  const router = useRouter();
  const {data} = useBlocksUserByIdQuery(group?.admin)
  return (
    <View className="bg-chalk rounded-xl border border-ash-3 p-4">
      <View className="flex-row items-center justify-between mb-2">
        <Text className="text-sm font-bold text-onyx">
          {t("LABEL_GROUP_INFORMATION")}
        </Text>
        {isAdmin && (
          <Pressable
            className="p-1.5 rounded-full hover:bg-ash-3"
            onPress={() => router.push(`/groups/${group.id}/edit`)}
          >
            <Edit2 size={18} color={themeVars["--color-primary-default"] as string} variant="Linear" />
          </Pressable>
        )}
      </View>

      <View className="divide-y divide-ash-3">
        <InfoRow
          icon={<Subtitle size={20} color={themeVars["--color-primary-default"] as string} />}
          label={t("LABEL_GROUP_TITLE")}
          value={group.name}
        />
        <InfoRow
          icon={<Profile size={20} color={themeVars["--color-primary-default"] as string} />}
          label={t("LABEL_GROUP_ADMIN")}
          value={formatUserName(data?.firstName, data?.lastName)}
        />
        <InfoRow
          icon={<Lock size={20} color={themeVars["--color-primary-default"] as string} />}
          label={t("LABEL_GROUP_VISIBILITY")}
          value={
            group.isPrivate ? t("LABEL_PRIVATE_GROUP") : t("LABEL_PUBLIC_GROUP")
          }
        />
        <InfoRow
          icon={<ReceiptText size={20} color={themeVars["--color-primary-default"] as string} />}
          label={t("LABEL_GROUP_DESCRIPTION")}
          value={group.description}
        />
      </View>
    </View>
  );
};

export default GroupInfoCard;
